import { useIsRestoring } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { setUnauthorizedHandler } from "@/lib/api";
import { ME_QUERY_KEY } from "@/lib/auth";
import { olvidarObraActiva } from "@/lib/obra-activa";
import {
  CACHE_BUSTER,
  CACHE_MAX_AGE_MS,
  offlinePersister,
  purgeOfflineCache,
  requestPersistentStorage,
  shouldPersistQuery,
} from "@/lib/offline-cache";
import { queryClient, router } from "@/router";
import "./index.css";

// Interceptor global de 401 (NO_AUTENTICADO): la sesión ha caducado. Se limpia la
// caché en memoria y la caché de lecturas en disco (aislamiento entre usuarios), y
// se redirige a /login conservando el deep-link.
// F4 (offline): esto NO descarta la outbox de mutaciones pendientes —vive en su
// propia DB— y solo se dispara online (offline un 401 no llega a ocurrir).
setUnauthorizedHandler(() => {
  queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
  void purgeOfflineCache();
  // #252: la obra activa recordada es contexto del usuario saliente y se olvida con él.
  olvidarObraActiva();
  const { pathname, href } = router.state.location;
  if (pathname !== "/login") {
    void router.navigate({ to: "/login", search: { redirect: href } });
  }
});

// Almacenamiento persistente best-effort: evita que el navegador desaloje la caché
// offline bajo presión de disco. No bloquea el arranque.
void requestPersistentStorage();

const persistOptions = {
  persister: offlinePersister,
  maxAge: CACHE_MAX_AGE_MS,
  buster: CACHE_BUSTER,
  dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
};

// RouterGate retrasa el montaje del router hasta que la caché persistida se ha
// rehidratado. Es clave para el offline: el beforeLoad de las rutas resuelve /me con
// ensureQueryData; sin esperar a la restauración, al abrir sin red /me no estaría en
// caché y se redirigiría a /login en lugar de hidratar la sesión guardada.
function RouterGate() {
  const isRestoring = useIsRestoring();
  if (isRestoring) {
    return null;
  }
  return <RouterProvider router={router} />;
}

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("No se encontró el elemento #root");
}

createRoot(rootEl).render(
  <StrictMode>
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <RouterGate />
    </PersistQueryClientProvider>
  </StrictMode>,
);
